import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import type { CreateWishlistItemRequest } from '../../types/wishlist';
import { useTheme } from '../../context/ThemeContext';

interface SubmitWishModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (wish: CreateWishlistItemRequest) => Promise<void>;
  isSubmitting: boolean;
  initialValue?: CreateWishlistItemRequest;
}

export default function SubmitWishModal({ isOpen, onClose, onSubmit, isSubmitting, initialValue }: SubmitWishModalProps) {
  const { theme } = useTheme();
  const isEditMode = !!initialValue;
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (isOpen) {
      setTitle(initialValue?.title || '');
      setDescription(initialValue?.description || '');
    }
  }, [isOpen, initialValue]);

  if (!isOpen) return null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting || !title.trim() || !description.trim()) return;

    await onSubmit({ title: title.trim(), description: description.trim() });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900">{isEditMode ? 'Edit Wish' : 'Submit a Wish'}</h2>
          <button onClick={onClose} disabled={isSubmitting} className="rounded-full p-1 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50" aria-label="Close">
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <p className="mb-4 text-sm text-gray-600">
            {isEditMode
              ? 'Update the title or description of this wish.'
              : "Tell us about a feature you'd like to see in ISentryx. Popular wishes help us prioritize what to build next."}
          </p>

          <div className="mb-4">
            <label htmlFor="wishTitle" className="mb-2 block text-sm font-medium text-gray-700">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              id="wishTitle"
              type="text"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              disabled={isSubmitting}
              placeholder="Short name for your feature idea"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-brand-primary"
              maxLength={150}
              style={{ backgroundColor: theme.surfaceHover, borderColor: theme.border, color: theme.text }}
            />
          </div>

          <div className="mb-4">
            <label htmlFor="wishDescription" className="mb-2 block text-sm font-medium text-gray-700">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="wishDescription"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              disabled={isSubmitting}
              placeholder="What should it do, and why would it help you?"
              rows={5}
              className="max-h-40 w-full resize-none overflow-y-auto rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-brand-primary"
              maxLength={2000}
              style={{ backgroundColor: theme.surfaceHover, borderColor: theme.border, color: theme.text }}
            />
            <p className="mt-1 text-xs text-gray-500">{description.length}/2000 characters</p>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 rounded-lg border border-gray-200 bg-white px-4 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !title.trim() || !description.trim()}
              className="flex-1 rounded-lg bg-brand-primary px-4 py-2 font-medium text-white transition-colors hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? (isEditMode ? 'Saving...' : 'Submitting...') : isEditMode ? 'Save Changes' : 'Submit Wish'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
